import { EVALUATION_TOOL } from '../../components/AutomationStudio/constant';
import { resolveRecordingReplayable, resolveRecordingStepStatus } from './recordingPlanMarkers';

/**
 * Merge saved execution record plan into instruction rows.
 * Supports executions[] (grouped) and steps[] (flat) formats.
 */
export function mergeExecutionRecordIntoInstructions(instructions = [], executionRecord) {
    if (!executionRecord) return instructions;

    const executionsByInstructionId = {};
    const executionGroups = Array.isArray(executionRecord.executions) ? executionRecord.executions : [];
    const planSteps = Array.isArray(executionRecord.steps) ? executionRecord.steps : [];

    if (executionGroups.length > 0) {
        executionGroups.forEach((executionGroup) => {
            const mainAction = executionGroup.mainAction;
            const instructionId = mainAction?.args?.instructionId;
            const instructionKey = instructionId != null ? String(instructionId) : null;
            if (!instructionKey) return;

            if (!executionsByInstructionId[instructionKey]) {
                executionsByInstructionId[instructionKey] = [];
            }

            const mainStep = {
                ...mainAction,
                id: `${executionGroup.id}-main`,
                executionId: executionGroup.id,
                description:
                    mainAction.details?.description ||
                    mainAction.args?.stepDescription ||
                    mainAction.tool,
                tool: mainAction.tool,
                confidence: mainAction.details?.confidence,
                stepType: 'ai_generated',
                arguments: mainAction.args || {},
                status: 'success',
                instructionId,
                details: mainAction.details,
                executionGroup,
                executionStrategies: mainAction.executionStrategies,
            };
            executionsByInstructionId[instructionKey].push(mainStep);

            if (Array.isArray(executionGroup.related)) {
                executionGroup.related.forEach((relatedAction, relatedIndex) => {
                    executionsByInstructionId[instructionKey].push({
                        ...relatedAction,
                        id: `${executionGroup.id}-related-${relatedIndex}`,
                        executionId: `${executionGroup.id}-related-${relatedIndex}`,
                        description:
                            relatedAction.details?.description ||
                            relatedAction.args?.stepDescription ||
                            relatedAction.tool,
                        tool: relatedAction.tool,
                        confidence: relatedAction.details?.confidence,
                        stepType: 'verification',
                        arguments: relatedAction.args || {},
                        status: 'success',
                        instructionId: relatedAction?.args?.instructionId ?? instructionId,
                        details: relatedAction.details,
                        executionStrategies: relatedAction.executionStrategies,
                    });
                });
            }
        });
    }

    if (executionGroups.length === 0 && planSteps.length > 0) {
        const validInstructionKeys = new Set(instructions.map((i) => String(i.id)));
        const lastInstructionKey = instructions.length > 0 ? String(instructions[instructions.length - 1].id) : null;
        const orderedInstructionKeys = instructions.map((i) => String(i.id));
        const evalSeenForKey = {};

        planSteps.forEach((planStep, index) => {
            let resolvedInstructionId =
                planStep?.instruction?.id ?? planStep?.args?.instructionId ?? planStep?.instructionId;
            let instructionKey = resolvedInstructionId != null ? String(resolvedInstructionId) : null;

            if (!instructionKey || !validInstructionKeys.has(instructionKey)) {
                if (lastInstructionKey) {
                    instructionKey = lastInstructionKey;
                    resolvedInstructionId = instructions[instructions.length - 1].id;
                } else {
                    return;
                }
            }

            if (planStep.tool === EVALUATION_TOOL) {
                if (evalSeenForKey[instructionKey]) {
                    const currentKeyIndex = orderedInstructionKeys.indexOf(instructionKey);
                    if (currentKeyIndex !== -1 && currentKeyIndex < orderedInstructionKeys.length - 1) {
                        const nextKey = orderedInstructionKeys[currentKeyIndex + 1];
                        if (validInstructionKeys.has(nextKey)) {
                            instructionKey = nextKey;
                            resolvedInstructionId = instructions[currentKeyIndex + 1].id;
                        }
                    }
                }
                evalSeenForKey[instructionKey] = true;
            } else if (planStep.tool?.startsWith('browser_') && planStep.tool !== 'browser_snapshot') {
                evalSeenForKey[instructionKey] = false;
            }

            if (!executionsByInstructionId[instructionKey]) {
                executionsByInstructionId[instructionKey] = [];
            }

            const numericConfidence =
                planStep.confidence ??
                planStep.args?.confidence ??
                planStep.extractedData?.evaluation?.confidence ??
                planStep.extractedData?.confidence;
            const screenshotPath =
                planStep.extractedData?.screenshotPath ??
                planStep.screenshotPath ??
                planStep.screenshot ??
                null;

            const resolvedStatus = resolveRecordingStepStatus(planStep);
            const resolvedReplayable = resolveRecordingReplayable(planStep, resolvedStatus);

            executionsByInstructionId[instructionKey].push({
                ...planStep,
                id: planStep.id || `saved-step-${instructionKey}-${index}`,
                description:
                    planStep.stepDescription ||
                    planStep.description ||
                    planStep.extractedData?.stepDescription ||
                    planStep.tool,
                tool: planStep.tool,
                confidence:
                    typeof numericConfidence === 'number' && Number.isFinite(numericConfidence)
                        ? numericConfidence
                        : undefined,
                stepType:
                    planStep.stepType ||
                    (planStep.tool === EVALUATION_TOOL ? 'verification' : 'ai_generated'),
                arguments: planStep.args || planStep.arguments || {},
                status: resolvedStatus,
                ...(typeof resolvedReplayable === 'boolean' && { replayable: resolvedReplayable }),
                instructionId: resolvedInstructionId,
                details: planStep.details || planStep.extractedData || {},
                executionStrategies: planStep.executionStrategies,
                screenshot: screenshotPath,
                screenshotPath,
            });
        });
    }

    let globalIdx = 0;
    return instructions.map((instruction) => {
        const instructionKey = String(instruction.id);
        const aiSteps = executionsByInstructionId[instructionKey] || [];
        const mergedSteps = aiSteps.length > 0 ? aiSteps : instruction.aiSteps;
        return {
            ...instruction,
            aiSteps: (mergedSteps || []).map((s) => ({ ...s, stepIndex: globalIdx++ })),
        };
    });
}

export function extractDynamicResultFromPlan(plan) {
    if (!plan) return null;
    return plan.metadata?.dynamicResult ?? plan.dynamicResults ?? null;
}
